/**
 * Pair With This Product - Premium Popup Controller
 * Features:
 * - Linked Product Title & Image to PDP (/products/{handle})
 * - "No Pre-Selected Size" policy (buttons start disabled with 'SELECT SIZE')
 * - Out-of-Stock strikethrough styling
 * - Theme-matched typography & responsive mobile optimization
 */

class PairProductsPopupManager {
  constructor() {
    this.overlay = null;
    this.container = null;
    this.content = null;
    this.closeBtn = null;
    this.cartRef = null;
    this.productCache = new Map();
    this.activeProductsState = [];

    document.addEventListener('DOMContentLoaded', () => this.init());
  }

  init() {
    this.overlay = document.getElementById('PairProductsPopupOverlay');
    if (!this.overlay) return;

    this.container = this.overlay.querySelector('.pair-popup-container');
    this.content = document.getElementById('PairPopupContent');
    this.closeBtn = document.getElementById('PairPopupCloseBtn');

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.hide(true));
    }

    this.overlay.addEventListener('click', (evt) => {
      if (evt.target === this.overlay) this.hide(true);
    });

    document.addEventListener('keydown', (evt) => {
      if (evt.key === 'Escape' && this.isOpen()) this.hide(true);
    });
  }

  isOpen() {
    return this.overlay && this.overlay.classList.contains('active');
  }

  /**
   * Main entry point called after add-to-cart succeeds on PDP
   */
  async show(handles, cartRef = null) {
    if (!handles || !Array.isArray(handles) || handles.length === 0) {
      if (cartRef && typeof cartRef.open === 'function') cartRef.open();
      return;
    }

    if (!this.overlay) this.init();
    this.cartRef = cartRef || document.querySelector('cart-drawer') || document.querySelector('cart-notification');

    this.overlay.classList.add('active');
    this.overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.renderLoading();

    try {
      const targetHandles = handles.slice(0, 2);
      const productPromises = targetHandles.map(handle => this.fetchProductJson(handle));
      const products = (await Promise.all(productPromises)).filter(Boolean);

      if (products.length === 0) {
        this.hide(true);
        return;
      }

      this.renderPopup(products);
    } catch (err) {
      console.error('Error rendering pair products popup:', err);
      this.hide(true);
    }
  }

  async fetchProductJson(handle) {
    if (this.productCache.has(handle)) {
      return this.productCache.get(handle);
    }
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return null;
      const data = await response.json();
      this.productCache.set(handle, data);
      return data;
    } catch (e) {
      console.error(`Failed to fetch product ${handle}`, e);
      return null;
    }
  }

  renderLoading() {
    if (!this.content) return;
    this.content.innerHTML = `
      <div class="pair-popup-loader">
        <div class="pair-popup-spinner"></div>
        <p>Loading matching recommendations...</p>
      </div>
    `;
  }

  hide(openCartDrawer = false) {
    if (!this.overlay) return;
    this.overlay.classList.remove('active');
    this.overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    if (openCartDrawer && this.cartRef && typeof this.cartRef.open === 'function') {
      setTimeout(() => {
        this.cartRef.open();
      }, 150);
    }
  }

  /**
   * Render either 1-product or 2-products layout
   * Policy: NO pre-selected variant initially
   */
  renderPopup(products) {
    this.activeProductsState = products.map(product => {
      return {
        product,
        selectedVariant: null, // No auto-selected size
        selectedOptions: {}
      };
    });

    const titleEl = document.getElementById('PairPopupTitle');
    if (titleEl) titleEl.textContent = 'Complete Your Look';

    if (products.length === 1) {
      this.renderSingleProductLayout(this.activeProductsState[0]);
    } else {
      this.renderDoubleProductLayout(this.activeProductsState);
    }
  }

  /* ==========================================================================
     1-PRODUCT LAYOUT RENDERER
     ========================================================================== */
  renderSingleProductLayout(state) {
    const { product } = state;
    const initialPrice = this.formatMoney(product.price);
    const comparePrice = product.compare_at_price > product.price ? this.formatMoney(product.compare_at_price) : '';
    const featuredImage = product.featured_image;
    const productUrl = `/products/${product.handle}`;

    this.content.innerHTML = `
      <div class="pair-layout-single">
        <!-- Linked Image -->
        <a href="${productUrl}" class="pair-single-media" title="View ${this.escapeHtml(product.title)}">
          <img id="PairSingleImg" src="${featuredImage}" alt="${this.escapeHtml(product.title)}" />
        </a>

        <!-- Right Product Info -->
        <div class="pair-single-details">
          <!-- Linked Product Title -->
          <h3 class="pair-product-title">
            <a href="${productUrl}" class="pair-title-link">
              ${this.escapeHtml(product.title)}
            </a>
          </h3>

          <!-- Rating -->
          <div class="pair-rating-row">
            <span class="pair-stars">★ ★ ★ ★ ★</span>
            <span class="pair-rating-score">4.7</span>
            <span class="pair-reviews-count">(128 reviews)</span>
          </div>

          <!-- Price -->
          <div class="pair-price-row">
            <span id="PairSinglePrice" class="pair-price">${initialPrice}</span>
            <span id="PairSingleComparePrice" class="pair-compare-price">${comparePrice}</span>
          </div>

          <!-- Options -->
          <div class="pair-options-container" id="PairSingleOptions">
            ${this.renderOptionsMarkup(product, state, 0)}
          </div>

          <!-- Action Buttons (Disabled initially until size selected) -->
          <div class="pair-single-actions">
            <button type="button" class="btn-pair-add" id="BtnPairSingleAdd" disabled>
              SELECT SIZE
            </button>
            <button type="button" class="btn-pair-buy-together" id="BtnPairSingleBuyTogether" disabled>
              BUY TOGETHER
            </button>
          </div>
        </div>

        <!-- Single Product Footer: No Thanks Continue -->
        <div class="pair-single-footer" style="grid-column: span 2; display: flex; justify-content: center; margin-top: 8px;">
          <button type="button" class="pair-continue-link" id="BtnPairSingleContinue">No thanks, continue</button>
        </div>
      </div>
    `;

    this.bindOptionListeners(0);

    // Bind Add to Cart
    document.getElementById('BtnPairSingleAdd').addEventListener('click', (e) => {
      if (!state.selectedVariant) return;
      this.handleSingleAddToCart(e.currentTarget, state.selectedVariant.id);
    });

    // Bind Buy Together
    document.getElementById('BtnPairSingleBuyTogether').addEventListener('click', () => {
      if (!state.selectedVariant) return;
      this.handleBuyTogether([state.selectedVariant.id]);
    });

    // Bind Continue Shopping Link
    const singleContinueBtn = document.getElementById('BtnPairSingleContinue');
    if (singleContinueBtn) {
      singleContinueBtn.addEventListener('click', () => this.hide(true));
    }
  }

  /* ==========================================================================
     2-PRODUCTS LAYOUT RENDERER
     ========================================================================== */
  renderDoubleProductLayout(states) {
    let cardsHtml = '';
    states.forEach((state, pIdx) => {
      const { product } = state;
      const initialPrice = this.formatMoney(product.price);
      const comparePrice = product.compare_at_price > product.price ? this.formatMoney(product.compare_at_price) : '';
      const featuredImage = product.featured_image;
      const productUrl = `/products/${product.handle}`;

      const badge = pIdx === 0 ? 'Best Seller' : 'New Arrival';
      const badgeClass = pIdx === 0 ? '' : 'new-arrival';

      cardsHtml += `
        <div class="pair-card" data-product-index="${pIdx}">
          <!-- Linked Image -->
          <div class="pair-card-image-wrap">
            <span class="pair-badge ${badgeClass}">${badge}</span>
            <button type="button" class="pair-wishlist-btn" title="Add to Wishlist">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#333" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
            </button>
            <a href="${productUrl}" style="display:block; width:100%; height:100%;">
              <img class="pair-card-img" src="${featuredImage}" alt="${this.escapeHtml(product.title)}" />
            </a>
          </div>

          <!-- Card Info -->
          <div class="pair-card-details">
            <!-- Linked Title -->
            <h4 class="pair-product-title" style="font-size: 15px;">
              <a href="${productUrl}" class="pair-title-link">
                ${this.escapeHtml(product.title)}
              </a>
            </h4>

            <!-- Rating -->
            <div class="pair-rating-row">
              <span class="pair-stars">★ ★ ★ ★ ★</span>
              <span class="pair-rating-score">${pIdx === 0 ? '4.6' : '4.5'}</span>
              <span class="pair-reviews-count">(${pIdx === 0 ? '98' : '76'} reviews)</span>
            </div>

            <!-- Price -->
            <div class="pair-price-row">
              <span class="pair-price pair-card-price">${initialPrice}</span>
              ${comparePrice ? `<span class="pair-compare-price">${comparePrice}</span>` : ''}
            </div>

            <!-- Options -->
            <div class="pair-options-container">
              ${this.renderOptionsMarkup(product, state, pIdx)}
            </div>

            <!-- Add to cart (disabled initially) -->
            <button type="button" class="btn-pair-add btn-card-add" style="margin-top: auto;" data-product-index="${pIdx}" disabled>
              SELECT SIZE
            </button>
          </div>
        </div>
      `;
    });

    this.content.innerHTML = `
      <div class="pair-layout-double">
        ${cardsHtml}

        <!-- Bottom Footer Callout -->
        <div class="pair-double-footer">
          <div class="pair-double-cta-group">
            <button type="button" class="btn-pair-buy-both" id="BtnPairBuyBoth" disabled>
              BUY BOTH TOGETHER
            </button>
          </div>

          <button type="button" class="pair-continue-link" id="BtnPairContinue">No thanks, continue</button>
        </div>
      </div>
    `;

    states.forEach((_, pIdx) => this.bindOptionListeners(pIdx));

    // Bind individual Add to Cart buttons
    this.content.querySelectorAll('.btn-card-add').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const pIdx = parseInt(btn.dataset.productIndex, 10);
        const state = this.activeProductsState[pIdx];
        if (!state.selectedVariant) return;
        this.handleSingleAddToCart(e.currentTarget, state.selectedVariant.id);
      });
    });

    // Bind Buy Both Together
    document.getElementById('BtnPairBuyBoth').addEventListener('click', () => {
      const selectedVariants = this.activeProductsState.map(s => s.selectedVariant).filter(Boolean);
      if (selectedVariants.length === 0) return;
      this.handleBuyTogether(selectedVariants.map(v => v.id));
    });

    // Bind Continue Shopping
    document.getElementById('BtnPairContinue').addEventListener('click', () => {
      this.hide(true);
    });
  }

  /* ==========================================================================
     OPTION PILL MARKUP & VARIANT SELECTION LOGIC
     ========================================================================== */
  renderOptionsMarkup(product, state, productIdx) {
    if (!product.options || product.options.length === 0) return '';

    return product.options.map((opt) => {
      const pillsHtml = opt.values.map(val => {
        const isOutOfStock = this.isValueOutOfStock(product, opt.name, val);
        const outOfStockClass = isOutOfStock ? 'out-of-stock' : '';

        return `
          <button type="button" 
                  class="pair-pill-btn ${outOfStockClass}" 
                  data-product-index="${productIdx}" 
                  data-option-name="${this.escapeHtml(opt.name)}" 
                  data-option-value="${this.escapeHtml(val)}">
            ${this.escapeHtml(val)}
          </button>
        `;
      }).join('');

      return `
        <div class="pair-option-group">
          <span class="pair-option-label">${this.escapeHtml(opt.name)}</span>
          <div class="pair-option-values">
            ${pillsHtml}
          </div>
        </div>
      `;
    }).join('');
  }

  isValueOutOfStock(product, optionName, optionValue) {
    const optIdx = product.options.findIndex(o => o.name === optionName);
    if (optIdx === -1) return false;
    const matchingVariants = product.variants.filter(v => v.options[optIdx] === optionValue);
    if (matchingVariants.length === 0) return true;
    return !matchingVariants.some(v => v.available);
  }

  bindOptionListeners(productIdx) {
    const pills = this.content.querySelectorAll(`.pair-pill-btn[data-product-index="${productIdx}"]`);
    pills.forEach(pill => {
      pill.addEventListener('click', (e) => {
        const btn = e.currentTarget;
        const optName = btn.dataset.optionName;
        const optVal = btn.dataset.optionValue;

        const state = this.activeProductsState[productIdx];
        state.selectedOptions[optName] = optVal;

        // Match variant
        const matchingVariant = state.product.variants.find(v => {
          return state.product.options.every((opt, idx) => {
            return v.options[idx] === state.selectedOptions[opt.name];
          });
        });

        state.selectedVariant = matchingVariant || null;
        this.updateCardUi(productIdx);
        this.updateGlobalBuyTogetherButton();
      });
    });
  }

  updateCardUi(productIdx) {
    const state = this.activeProductsState[productIdx];
    const { selectedVariant } = state;

    // Update active pill highlight
    const pills = this.content.querySelectorAll(`.pair-pill-btn[data-product-index="${productIdx}"]`);
    pills.forEach(pill => {
      const optName = pill.dataset.optionName;
      const optVal = pill.dataset.optionValue;
      pill.classList.toggle('selected', state.selectedOptions[optName] === optVal);
    });

    if (this.activeProductsState.length === 1) {
      const priceEl = document.getElementById('PairSinglePrice');
      const compareEl = document.getElementById('PairSingleComparePrice');
      const imgEl = document.getElementById('PairSingleImg');
      const addBtn = document.getElementById('BtnPairSingleAdd');
      const buyTogetherBtn = document.getElementById('BtnPairSingleBuyTogether');

      if (selectedVariant) {
        if (priceEl) priceEl.textContent = this.formatMoney(selectedVariant.price);
        if (compareEl) {
          compareEl.textContent = selectedVariant.compare_at_price > selectedVariant.price ? this.formatMoney(selectedVariant.compare_at_price) : '';
        }
        if (imgEl && selectedVariant.featured_image) {
          imgEl.src = selectedVariant.featured_image.src;
        }

        if (selectedVariant.available) {
          if (addBtn) {
            addBtn.disabled = false;
            addBtn.innerHTML = `ADD TO CART`;
          }
          if (buyTogetherBtn) buyTogetherBtn.disabled = false;
        } else {
          if (addBtn) {
            addBtn.disabled = true;
            addBtn.innerHTML = `OUT OF STOCK`;
          }
          if (buyTogetherBtn) buyTogetherBtn.disabled = true;
        }
      }
    } else {
      const card = this.content.querySelector(`.pair-card[data-product-index="${productIdx}"]`);
      if (!card) return;

      const priceEl = card.querySelector('.pair-card-price');
      const imgEl = card.querySelector('.pair-card-img');
      const addBtn = card.querySelector('.btn-card-add');

      if (selectedVariant) {
        if (priceEl) priceEl.textContent = this.formatMoney(selectedVariant.price);
        if (imgEl && selectedVariant.featured_image) {
          imgEl.src = selectedVariant.featured_image.src;
        }

        if (selectedVariant.available) {
          if (addBtn) {
            addBtn.disabled = false;
            addBtn.innerHTML = `ADD TO CART`;
          }
        } else {
          if (addBtn) {
            addBtn.disabled = true;
            addBtn.innerHTML = `OUT OF STOCK`;
          }
        }
      }
    }
  }

  updateGlobalBuyTogetherButton() {
    const buyBothBtn = document.getElementById('BtnPairBuyBoth');
    if (!buyBothBtn) return;

    const selectedAvailable = this.activeProductsState.filter(s => s.selectedVariant && s.selectedVariant.available);
    buyBothBtn.disabled = selectedAvailable.length === 0;
  }

  /* ==========================================================================
     AJAX CART OPERATIONS
     ========================================================================== */
  async handleSingleAddToCart(buttonEl, variantId) {
    const origHtml = buttonEl.innerHTML;
    buttonEl.disabled = true;
    buttonEl.innerHTML = 'Adding...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });

      if (response.ok) {
        buttonEl.innerHTML = 'Added ✓';
        buttonEl.style.backgroundColor = '#166534';
        buttonEl.style.color = '#ffffff';
        buttonEl.style.borderColor = '#166534';

        this.refreshCartDrawer();

        setTimeout(() => {
          buttonEl.disabled = false;
          buttonEl.innerHTML = origHtml;
          buttonEl.style.backgroundColor = '';
          buttonEl.style.color = '';
          buttonEl.style.borderColor = '';
        }, 2000);
      } else {
        const errData = await response.json();
        alert(errData.description || 'Error adding product to cart.');
        buttonEl.disabled = false;
        buttonEl.innerHTML = origHtml;
      }
    } catch (err) {
      console.error('Add to cart failed', err);
      buttonEl.disabled = false;
      buttonEl.innerHTML = origHtml;
    }
  }

  async handleBuyTogether(variantIds) {
    const items = variantIds.map(id => ({ id, quantity: 1 }));
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (response.ok) {
        await this.refreshCartDrawer();
        this.hide(true);
      } else {
        const errData = await response.json();
        alert(errData.description || 'Failed to add items together.');
      }
    } catch (err) {
      console.error('Buy together failed', err);
    }
  }

  async refreshCartDrawer() {
    if (!this.cartRef) {
      this.cartRef = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    }

    if (this.cartRef) {
      try {
        const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        const newCartDrawer = html.querySelector('cart-drawer');
        if (newCartDrawer && this.cartRef.querySelector('.drawer__inner')) {
          this.cartRef.querySelector('.drawer__inner').innerHTML = newCartDrawer.querySelector('.drawer__inner').innerHTML;
        }
      } catch (e) {
        console.warn('Could not refresh cart drawer section', e);
      }

      // Unlock checkout button immediately
      const checkoutBtns = this.cartRef.querySelectorAll('.gokwik-checkout button, #CartDrawer-Checkout, .cart__checkout-button');
      checkoutBtns.forEach(btn => {
        btn.disabled = false;
        btn.removeAttribute('disabled');
        btn.classList.remove('disabled');
        const loader = btn.querySelector('.addloadr#btn-loader, #btn-loader');
        if (loader) loader.style.setProperty('display', 'none', 'important');
      });

      // Open Cart Drawer
      if (typeof this.cartRef.open === 'function') {
        this.cartRef.open();
      } else {
        this.cartRef.classList.add('active');
        document.body.classList.add('overflow-hidden');
      }
    }

    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  /* ==========================================================================
     UTILITIES
     ========================================================================== */
  formatMoney(cents) {
    if (typeof cents === 'string') cents = parseInt(cents, 10);
    const dollars = (cents / 100).toFixed(2);
    return `₹${dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  }

  escapeHtml(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}

// Global Singleton Instance
window.PairProductsPopup = new PairProductsPopupManager();
